import _get from 'lodash/get';
import { createSelector } from 'reselect';

import * as authActions from 'modules/auth/actions';
import { createReducer } from 'utils/reducer';

import * as mfaActions from './actions';
import * as helpers from './reducerHelpers';
import INITIAL_STATE from './initialState';

/**
 * Build the map of 'mfa' redux handlers.
 *
 * @type {Object}
 */
const mfa = {
    [authActions.logout.SUCCESS]: helpers.clearMFASession,
    [mfaActions.clearMFAError.TRIGGER]: helpers.clearMFAError,
    [mfaActions.clearMFASession.TRIGGER]: helpers.clearMFASession,
    [mfaActions.setMFASession.TRIGGER]: helpers.setMFASession,
    [mfaActions.verifyMFACode.ERROR]: helpers.verifyMFACodeError,
    [mfaActions.verifyMFACode.REQUEST]: helpers.verifyMFACodeRequest,
};

// Create and export the 'mfa' reducer.
// NOTE: The default export will be the reducer.
export default createReducer(INITIAL_STATE, mfa);

// SELECTORS

/**
 * Get the complete 'mfa' state.
 *
 * @method getMFA
 * @param  {Object} state The app state
 * @return {Object}       The mfa state
 */
export const getMFA = state => _get(state, 'mfa', INITIAL_STATE);

/**
 * Get the short lived MFA token.
 *
 * @method getMFAToken
 * @return {string} The mfa token
 */
export const getMFAToken = createSelector([getMFA], mfaState => _get(mfaState, 'mfaToken', ''));

/**
 * Get the MFA method the user has enrolled.
 *
 * @method getMFAMethod
 * @return {string} The mfa method
 */
export const getMFAMethod = createSelector([getMFA], mfaState => _get(mfaState, 'method', ''));

/**
 * Get the MFA methods the user is allowed to enroll into.
 *
 * @method getMFAAllowedMethods
 * @return {Array} The allowed methods
 */
export const getMFAAllowedMethods = createSelector([getMFA], mfaState =>
    _get(mfaState, 'allowedMethods', [])
);

/**
 * Get the `status` returned by the authenticate API.
 *
 * @method getMFAStatus
 * @return {string} The mfa status
 */
export const getMFAStatus = createSelector([getMFA], mfaState => _get(mfaState, 'status', ''));

/**
 * Get the login credentials kept for the post sign in redirect.
 *
 * @method getMFALoginContext
 * @return {Object} The login context
 */
export const getMFALoginContext = createSelector([getMFA], mfaState =>
    _get(mfaState, 'loginContext', {})
);

/**
 * Get the error of the last verification attempt.
 *
 * @method getMFAError
 * @return {Object|null} The error
 */
export const getMFAError = createSelector([getMFA], mfaState => _get(mfaState, 'error', null));

/**
 * Get the attempts left in the current challenge, as reported by the API.
 *
 * NOTE: the value outlives the error it came with, so the screen keeps showing
 * it once the error itself has been cleared.
 *
 * @method getMFARemainingAttempts
 * @return {number|null} The attempts left
 */
export const getMFARemainingAttempts = createSelector([getMFA], mfaState =>
    _get(mfaState, 'remainingAttempts', null)
);

/**
 * Determine whether a verification request is in flight.
 *
 * @method isVerifyingMFACode
 * @return {boolean}
 */
export const isVerifyingMFACode = createSelector([getMFA], mfaState =>
    Boolean(_get(mfaState, 'isVerifying', false))
);

/**
 * Determine whether there is an MFA session waiting to be completed.
 *
 * @method hasPendingMFASession
 * @return {boolean}
 */
export const hasPendingMFASession = createSelector([getMFAToken], Boolean);
